import React, { useState } from "react";
import "./FinalStep.css";
import config from "../../../../config";
import { toast } from "react-toastify";
import api from "../../../../hooks/api";
import axios from "axios";

export default function FinalStep({
  productData,
  propertiesData,
  pricingData,
  imagesData,
  deletedImages,
  onPrev,
  onSubmit,
  onNewProduct,
  business_id,
  onClose
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");
  const [validationError, setValidationError] = useState("");

  const sanitizePricingForServer = (data) => {
    if (!data || !data.propertyData) return data;

    const cleanData = JSON.parse(JSON.stringify(data));

    const toEnglishNumber = (str) => {
      if (typeof str === 'number') return str;
      if (!str) return 0;
      const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
      let englishStr = str.toString();
      for (let i = 0; i < 10; i++) {
        englishStr = englishStr.replace(new RegExp(persianDigits[i], 'g'), i);
      }
      return englishStr.replace(/[^0-9.-]/g, '');
    };

    Object.keys(cleanData.propertyData).forEach(key => {
      cleanData.propertyData[key] = cleanData.propertyData[key].map(item => ({
        ...item,
        price: Number(item.rawPrice || toEnglishNumber(item.price)),
        rawPrice: Number(item.rawPrice || toEnglishNumber(item.price)),
        stock: Number(toEnglishNumber(item.stock))
      }));
    });

    return cleanData;
  };

  const handleSubmit = async () => {
    if (!imagesData || !imagesData.images || imagesData.images.length === 0) {
      const errMsg = "برای ثبت کالا، انتخاب حداقل یک تصویر (به عنوان تصویر پیش‌فرض) الزامی است.";
      setValidationError(errMsg);

      toast.warning(errMsg, {
        position: "top-center",
        autoClose: 4000,
        theme: "colored",
        rtl: true,
        style: { zIndex: 999999 }
      });
      return;
    }

    setValidationError("");
    setIsSubmitting(true);
    let finalDefaultImagePath = null;
    let finalGalleryPaths = [];

    try {
      const base64ToFile = (base64, filename) => {
        const arr = base64.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        return new File([u8arr], filename, { type: mime });
      };

      const filesToUpload = [];
      const requestedFiles = [];

      if (imagesData.images && imagesData.images.length > 0) {
        const mainImgServer = imagesData.images.find(img => img.isMain && !img.url.startsWith('data:image'));
        if (mainImgServer) {
          finalDefaultImagePath = mainImgServer.url.replace(`${config.api.imageURL}/`, '');
        }

        const mainImageNew = imagesData.images.find(img => img.isMain && img.url.startsWith('data:image'));
        if (mainImageNew) {
          const file = base64ToFile(mainImageNew.url, `main_${mainImageNew.fileName || 'image.jpg'}`);
          const ext = file.name.split('.').pop().toLowerCase();
          requestedFiles.push({ extension: ext, type: 'default', business_id: business_id });
          filesToUpload.push({ file, type: 'default' });
        }

        const galleryImagesNew = imagesData.images.filter(img => !img.isMain && img.url.startsWith('data:image'));
        galleryImagesNew.forEach((img, index) => {
          const file = base64ToFile(img.url, `gallery_${index}_${img.fileName || 'image.jpg'}`);
          const ext = file.name.split('.').pop().toLowerCase();
          requestedFiles.push({ extension: ext, type: 'gallery', business_id: business_id });
          filesToUpload.push({ file, type: 'gallery' });
        });
      }

      if (requestedFiles.length > 0) {
        const linkResponse = await api.post('commodity/upload/presigned-url', { files: requestedFiles });
        const uploadInfos = linkResponse.data;

        const uploadPromises = uploadInfos.map((uploadInfo, i) => {
          const { file, type } = filesToUpload[i];
          return axios.put(uploadInfo.upload_url, file, {
            headers: {
              'Content-Type': file.type,
              'x-amz-acl': 'public-read'
            }
          }).then(() => ({ type, filePath: uploadInfo.file_path }));
        });

        const uploadResults = await Promise.all(uploadPromises);

        uploadResults.forEach(result => {
          if (result.type === 'default') finalDefaultImagePath = result.filePath;
          if (result.type === 'gallery') finalGalleryPaths.push(result.filePath);
        });
      }

      const deletedImageUrls = deletedImages
        .map(img => img.url.replace(config.api.imageURL + '/', ''))
        .filter(url => url && url.trim() !== '' && !url.startsWith('data:image'));

      const finalPricingData = sanitizePricingForServer(pricingData);

      const payload = {
        id_commodity: productData.id_commodity == 0 ? null : productData.id_commodity,
        title: productData.name,
        VAT: Number(productData.vatPercent),
        pack_cost: Number(productData.packagingCost),
        discount_percent: Number(productData.discount),
        extraTitle: productData.shortDesc,
        description: productData.fullDesc,
        properties: finalPricingData,
        properties_title: propertiesData,
        id_business: business_id,
        id_commodityGroup: productData.category ?? null,
        instagram_post_url: productData.instagram,
        commodity_code: productData.sku,
        image_default: finalDefaultImagePath,
        gallery: finalGalleryPaths,
        gallery_delete: deletedImageUrls
      };

      const response = await api.post(`commodity/store`, payload);

      if (response.data.success) {
        toast.success(response.data.message, { position: "top-right", autoClose: 1000, theme: "light", rtl: true });

        const productId = response.data.data?.id || productData.id_commodity;
        const link = `${config.api.LinkApp}app?sId=${business_id}&pId=${productId}`;

        setGeneratedLink(link);
        setIsSubmitted(true);

        if (onSubmit) {
          onSubmit({ productId: productId, link: link, timestamp: new Date().toISOString() });
        }
      } else {
        toast.error(response.data.message, { position: "top-left", autoClose: 1000, theme: "light", rtl: true });
      }
    } catch (error) {
      console.error('Error submitting product:', error);
      toast.error('خطا در ارتباط با سرور ابری یا ثبت داده‌ها', { position: "top-left", autoClose: 1000, theme: "light", rtl: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedLink);
    toast.info("لینک کپی شد!", { position: "top-center", autoClose: 1500, theme: "light", rtl: true });
  };

  const handleNewProduct = () => {
    if (onNewProduct) {
      onNewProduct();
    } else {
      window.location.reload();
    }
  };

  if (isSubmitted) {
    return (
      <div className="fs-success-container">
        <div className="fs-success-content">
          <div className="fs-success-icon">🎉</div>
          <h2 className="fs-success-title">کالا با موفقیت ثبت شد!</h2>
          <p className="fs-success-message">کالای شما در سیستم ثبت شد و آماده نمایش است.</p>

          <div className="fs-generated-link">
            <label className="fs-link-label">لینک دسترسی به کالا:</label>
            <div className="fs-link-container">
              <input type="text" value={generatedLink} readOnly className="fs-link-input" dir="ltr" />
              <button className="fs-copy-btn" onClick={copyToClipboard}>📋 کپی لینک</button>
            </div>
          </div>

          <div className="fs-success-actions">
            {/* دکمه بستن و مشاهده کالا که لیست را نیز رفرش می‌کند */}
            <button
              className="fs-view-product-btn"
              onClick={() => {
                // ارسال رویداد رفرش لیست قبل از بستن مودال
                window.dispatchEvent(new Event('refreshProductsList'));
                if (onClose) {
                  onClose();
                }
              }}
            >
              👁️ بستن و مشاهده کالا
            </button>

            <button className="fs-new-product-btn" onClick={handleNewProduct}>➕ کالای جدید</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fs-container" dir="rtl">
      <div className="fs-header" style={{ textAlign: 'center', margin: '40px 0' }}>
        <div style={{ fontSize: '48px', marginBottom: '15px' }}>📦</div>
        <h2 className="fs-title" style={{ fontSize: '24px', color: '#333' }}>ثبت نهایی کالا</h2>
        <p className="fs-description" style={{ fontSize: '16px', color: '#666', marginTop: '10px' }}>
          اطلاعات کالای شما تکمیل شده است. آیا از ثبت آن در فروشگاه اطمینان دارید؟
        </p>
      </div>

      {validationError && (
        <div style={{
          backgroundColor: '#ffebee',
          color: '#c62828',
          padding: '12px',
          borderRadius: '8px',
          textAlign: 'center',
          marginBottom: '20px',
          fontWeight: 'bold',
          border: '1px solid #ffcdd2'
        }}>
          ⚠️ {validationError}
        </div>
      )}

      <div className="fs-actions" style={{ justifyContent: 'center', gap: '20px', marginTop: '10px' }}>
        <button
          className="fs-submit-btn"
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{ minWidth: '180px' }}
        >
          {isSubmitting ? (
            <>
              <span className="fs-loading-spinner"></span>
              در حال ثبت...
            </>
          ) : (
            <>
              <span className="fs-btn-icon">✅</span>
              بله، ثبت نهایی شود
            </>
          )}
        </button>
        <button
          className="fs-prev-btn"
          onClick={onPrev}
          disabled={isSubmitting}
        >
          <span className="fs-btn-icon">→</span>
          بازگشت به مراحل قبل
        </button>


      </div>
    </div>
  );
}
